// src/components/Entregas/StockBodegas.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  Table,
  Input,
  Modal,
  Button,
  Space,
  Typography,
  Tag,
  Progress,
  message,
  Descriptions,
  Statistic,
  Card,
  Row,
  Col,
  Divider,
  Select,
  Tooltip,
} from "antd";

import {
  SearchOutlined,
  EyeOutlined,
  ReloadOutlined,
  InboxOutlined,
  NumberOutlined,
  CalendarOutlined,
  ShopOutlined,
  BarcodeOutlined,
  GoldOutlined,
  PercentageOutlined,
  CheckCircleTwoTone,
  ExclamationCircleTwoTone,
  DollarCircleOutlined,
  FileExcelOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import * as XLSX from "xlsx";

import { getStockOrdenesServicios } from "../../services/entregas/servicesOrdenesCompra";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const currency = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return "N/A";
  return n.toLocaleString("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 2,
  });
};

const StockBodegas = () => {
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);

  const [selected, setSelected] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Nuevos estados para filtros
  const [estadoFilter, setEstadoFilter] = useState("todos");
  const [proveedorFilter, setProveedorFilter] = useState("todos");
  const [numeroOrdenFilter, setNumeroOrdenFilter] = useState("todos");

  const fetchData = async () => {
    try {
      setLoading(true);
      const resp = await getStockOrdenesServicios();
      const rows = Array.isArray(resp?.data) ? resp.data : [];
      setData(rows);
      setFiltered(rows);
    } catch (err) {
      console.error("[StockBodegas] Error fetching:", err);
      message.error("No se pudo cargar el stock.");
      setData([]);
      setFiltered([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Calcular indicadores
  const indicators = useMemo(() => {
    const totalLotes = data.length;
    const disponibles = data.filter(
      (r) => (r.disponible_asignar ?? 0) > 0
    ).length;
    const noDisponibles = data.filter(
      (r) => (r.disponible_asignar ?? 0) === 0
    ).length;
    const totalEntrada = data.reduce(
      (sum, r) => sum + (Number(r.cantidad_entrada) || 0),
      0
    );
    const totalAsignado = data.reduce(
      (sum, r) => sum + (Number(r.cantidad_asignada) || 0),
      0
    );
    const totalDisponible = data.reduce(
      (sum, r) => sum + (Number(r.disponible_asignar) || 0),
      0
    );
    const valorTotal = data.reduce((sum, r) => {
      const cantidad = Number(r.cantidad_entrada) || 0;
      const valor = Number(r.valor_unitario) || 0;
      return sum + cantidad * valor;
    }, 0);

    return {
      totalLotes,
      disponibles,
      noDisponibles,
      totalEntrada,
      totalAsignado,
      totalDisponible,
      valorTotal,
      porcentajeDisponible:
        totalLotes > 0 ? (disponibles / totalLotes) * 100 : 0,
    };
  }, [data]);

  // Obtener lista única de proveedores
  const proveedoresList = useMemo(() => {
    const uniqueProveedores = [
      ...new Set(data.map((r) => r.proveedor_nombre).filter(Boolean)),
    ];
    return uniqueProveedores.sort();
  }, [data]);

  // Obtener lista única de numero_orden
  const numeroOrdenList = useMemo(() => {
    const uniqueNumeroOrden = [
      ...new Set(data.map((r) => r.numero_orden).filter(Boolean)),
    ];
    return uniqueNumeroOrden.sort();
  }, [data]);

  const handleSearch = (value) => {
    setSearchText(value);
    applyFilters(value, estadoFilter, proveedorFilter, numeroOrdenFilter);
  };

  const handleEstadoFilter = (value) => {
    setEstadoFilter(value);
    applyFilters(searchText, value, proveedorFilter, numeroOrdenFilter);
  };

  const handleProveedorFilter = (value) => {
    setProveedorFilter(value);
    applyFilters(searchText, estadoFilter, value, numeroOrdenFilter);
  };

  const handleNumeroOrdenFilter = (value) => {
    setNumeroOrdenFilter(value);
    applyFilters(searchText, estadoFilter, proveedorFilter, value);
  };

  const applyFilters = (search, estado, proveedor, numeroOrden) => {
    let result = [...data];

    // Filtro de búsqueda
    if (search) {
      const term = search.toLowerCase();
      result = result.filter((r) => {
        const fields = [
          r.productoPO,
          r.numero_orden,
          r.proveedor_nombre,
          r.oidPo?.toString(),
          r.oidPo_Producto?.toString(),
        ];
        return fields.some((f) =>
          (f || "").toString().toLowerCase().includes(term)
        );
      });
    }

    // Filtro de estado
    if (estado !== "todos") {
      result = result.filter((r) => {
        const disponible = r.disponible_asignar ?? 0;
        if (estado === "disponible") return disponible > 0;
        if (estado === "no_disponible") return disponible === 0;
        return true;
      });
    }

    // Filtro de proveedor
    if (proveedor !== "todos") {
      result = result.filter((r) => r.proveedor_nombre === proveedor);
    }

    // Filtro de proveedor
    if (numeroOrden !== "todos") {
      result = result.filter((r) => r.numero_orden === numeroOrden);
    }

    setFiltered(result);
    setCurrentPage(1); // Resetear a primera página
  };

  const clearFilters = () => {
    setSearchText("");
    setNumeroOrdenFilter("todos");
    setProveedorFilter("todos");
    setEstadoFilter("todos");
  };

  const handleExport = () => {
    try {
      const exportData = filtered.map((r) => ({
        Producto: r.productoPO || "",
        "Unidad Medida": r.unidad_medida || "",
        "Número Orden": r.numero_orden || "",
        "Fecha PO": r.fecha_po ? dayjs(r.fecha_po).format("DD/MM/YYYY") : "",
        Proveedor: r.proveedor_nombre || "",
        "Identificación Proveedor": r.proveedor_identificacion || "",
        "Cantidad PO": r.cantidad_po ?? 0,
        "Cantidad Asignada": r.cantidad_asignada ?? 0,
        Disponible: r.disponible_asignar ?? 0,
        "% Asignado": r.porcentaje_asignado ?? 0,
        "Valor Unitario": r.valor_unitario ?? 0,
        "Valor Total Lote": (r.cantidad_entrada ?? 0) * (r.valor_unitario ?? 0),
        Estado:
          (r.disponible_asignar ?? 0) > 0 ? "Disponible" : "No Disponible",
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Stock Bodegas");

      // Ajustar ancho de columnas
      const colWidths = [
        { wch: 40 }, // Producto
        { wch: 15 }, // Unidad Medida
        { wch: 15 }, // Número Orden
        { wch: 12 }, // Fecha PO
        { wch: 25 }, // Proveedor
        { wch: 18 }, // Identificación
        { wch: 12 }, // Cantidad PO
        { wch: 15 }, // Cantidad Asignada
        { wch: 12 }, // Disponible
        { wch: 12 }, // % Asignado
        { wch: 15 }, // Valor Unitario
        { wch: 15 }, // Valor Total
        { wch: 15 }, // Estado
      ];
      ws["!cols"] = colWidths;

      const fileName = `Stock_Bodegas_${dayjs().format(
        "YYYY-MM-DD_HHmmss"
      )}.xlsx`;
      XLSX.writeFile(wb, fileName);
      message.success("Archivo exportado exitosamente");
    } catch (err) {
      console.error("Error al exportar:", err);
      message.error("Error al exportar el archivo");
    }
  };

  const columns = [
    {
      title: "Estado",
      key: "estado",
      align: "center",
      width: 100,
      fixed: "left",
      render: (_, record) => {
        const disponible = record.disponible_asignar ?? 0;
        return disponible > 0 ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Disponible
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="error">
            No Disponible
          </Tag>
        );
      },
    },
    {
      title: "Detalle",
      key: "detalle",
      align: "center",
      width: 100,
      fixed: "left",
      render: (_, record) => (
        <Button
          icon={<EyeOutlined />}
          size="small"
          onClick={() => {
            setSelected(record);
            setModalVisible(true);
          }}
        >
          Ver
        </Button>
      ),
    },
    {
      title: "Producto (PO)",
      dataIndex: "productoPO",
      key: "productoPO",
      width: 360,
      onCell: () => ({
        style: {
          maxWidth: 360,
          whiteSpace: "normal",
          wordBreak: "break-word",
          overflowWrap: "anywhere",
        },
      }),
      render: (txt, rec) => {
        const content = txt || "—";
        return (
          <>
            <Paragraph
              style={{ marginBottom: 4 }}
              ellipsis={{ rows: 2, tooltip: content }}
            >
              <Text strong>{content}</Text>
            </Paragraph>
            <Text type="secondary">Unidad: {rec.unidad_medida ?? "—"}</Text>
          </>
        );
      },
    },
    {
      title: "Orden",
      key: "orden",
      width: 150,
      render: (_, rec) => (
        <span>
          <Text>{rec.numero_orden || "—"}</Text>
          <br />
          <Text type="secondary">
            {rec.fecha_po ? dayjs(rec.fecha_po).format("DD/MM/YYYY") : "—"}
          </Text>
        </span>
      ),
    },
    {
      title: "Proveedor",
      key: "proveedor",
      width: 300,
      render: (_, r) => {
        const nombre = r.proveedor_nombre || "—";
        const value = r.proveedor_identificacion || r.proveedor_correo || "—";

        return (
          <span style={{ display: "flex", flexDirection: "column" }}>
            <Typography.Text strong style={{ marginBottom: 4 }}>
              {nombre}
            </Typography.Text>
            <Tooltip title={value}>
              <Typography.Text
                type="secondary"
                ellipsis={{ tooltip: false }} // 👈 corta con "..."
                style={{ fontSize: 12, maxWidth: 220 }}
              >
                {value}
              </Typography.Text>
            </Tooltip>
          </span>
        );
      },
    },
    {
      title: "Cant. PO",
      dataIndex: "cantidad_po",
      key: "cantidad_po",
      align: "center",
      width: 100,
      render: (v) => (v == null ? "—" : Number(v).toLocaleString("es-CO")),
    },
    {
      title: "Asignada",
      dataIndex: "cantidad_asignada",
      key: "cantidad_asignada",
      align: "center",
      width: 100,
      render: (v) => (v == null ? "—" : Number(v).toLocaleString("es-CO")),
    },
    {
      title: "Disponible",
      dataIndex: "disponible_asignar",
      key: "disponible_asignar",
      align: "center",
      width: 100,
      render: (v) => {
        const n = Number(v) || 0;
        return (
          <Tag color={n > 0 ? "green" : "red"}>{n.toLocaleString("es-CO")}</Tag>
        );
      },
    },
    {
      title: "% Asignado",
      dataIndex: "porcentaje_asignado",
      key: "porcentaje_asignado",
      align: "center",
      width: 140,
      render: (v) => {
        const p = Number(v) || 0;
        return (
          <div style={{ minWidth: 120 }}>
            <Progress percent={Math.max(0, Math.min(100, p))} size="small" />
          </div>
        );
      },
    },
    {
      title: "Valor Unitario",
      dataIndex: "valor_unitario",
      key: "valor_unitario",
      align: "right",
      width: 140,
      render: (v) => currency(v),
    },
  ];

  return (
    <div style={{ padding: "24px", background: "#f0f2f5", minHeight: "100vh" }}>
      <Card
        style={{ margin: "0 24px" }}
        title={
          <span style={{ fontSize: "20px", fontWeight: "bold" }}>
            📊 Stock por lote PO
          </span>
        }
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchData}
              loading={loading}
            >
              Actualizar
            </Button>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExport}
              disabled={filtered.length === 0}
            >
              Exportar Excel
            </Button>
          </Space>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={4}>
            <Card size="small">
              <Statistic
                title="Total Entrada"
                value={indicators.totalEntrada}
                precision={0}
                valueStyle={{ fontSize: 16 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Card size="small">
              <Statistic
                title="Total Asignado"
                value={indicators.totalAsignado}
                precision={0}
                valueStyle={{ fontSize: 16, color: "#fa8c16" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Card size="small">
              <Statistic
                title="Total Disponible"
                value={indicators.totalDisponible}
                precision={0}
                valueStyle={{ fontSize: 16, color: "#52c41a" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Valor Total Stock"
                value={indicators.valorTotal}
                prefix={<DollarCircleOutlined />}
                precision={0}
                valueStyle={{ fontSize: 16, color: "#faad14" }}
                formatter={(value) =>
                  `$${Number(value).toLocaleString("es-CO")}`
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text type="secondary">% General Asignado</Text>
                <Progress
                  percent={
                    indicators.totalEntrada > 0
                      ? (
                          (indicators.totalAsignado / indicators.totalEntrada) *
                          100
                        ).toFixed(1)
                      : 0
                  }
                  status="active"
                />
              </Space>
            </Card>
          </Col>
        </Row>

        <Card title="Filtros" size="small" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Select
                value={numeroOrdenFilter}
                onChange={handleNumeroOrdenFilter}
                style={{ width: "100%" }}
                placeholder="Filtrar por nnúmero de orden"
                showSearch
                optionFilterProp="children"
              >
                <Option value="todos">Numero de ordenes</Option>
                {numeroOrdenList.map((orden) => (
                  <Option key={orden} value={orden}>
                    {orden}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Input
                placeholder="Buscar por producto, orden o proveedor..."
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                allowClear
                style={{ width: "100%" }}
                prefix={<SearchOutlined />}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                value={estadoFilter}
                onChange={handleEstadoFilter}
                style={{ width: "100%" }}
                placeholder="Filtrar por estado"
              >
                <Option value="todos">Todos los estados</Option>
                <Option value="disponible">
                  <CheckCircleOutlined style={{ color: "#52c41a" }} />{" "}
                  Disponible
                </Option>
                <Option value="no_disponible">
                  <CloseCircleOutlined style={{ color: "#ff4d4f" }} /> No
                  Disponible
                </Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select
                value={proveedorFilter}
                onChange={handleProveedorFilter}
                style={{ width: "100%" }}
                placeholder="Filtrar por proveedor"
                showSearch
                optionFilterProp="children"
              >
                <Option value="todos">Todos los proveedores</Option>
                {proveedoresList.map((proveedor) => (
                  <Option key={proveedor} value={proveedor}>
                    {proveedor}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={2}>
              <Button onClick={clearFilters} block icon={<ClearOutlined />}>
                Limpiar
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Información de filtros activos */}
        {(estadoFilter !== "todos" ||
          proveedorFilter !== "todos" ||
          numeroOrdenFilter !== "todos" ||
          searchText) && (
          <Space style={{ marginBottom: 16 }} wrap>
            <Text type="secondary">Filtros activos:</Text>
            {searchText && (
              <Tag closable onClose={() => handleSearch("")}>
                Búsqueda: {searchText}
              </Tag>
            )}
            {estadoFilter !== "todos" && (
              <Tag closable onClose={() => handleEstadoFilter("todos")}>
                Estado:{" "}
                {estadoFilter === "disponible" ? "Disponible" : "No Disponible"}
              </Tag>
            )}
            {proveedorFilter !== "todos" && (
              <Tag closable onClose={() => handleProveedorFilter("todos")}>
                Proveedor: {proveedorFilter}
              </Tag>
            )}

            {numeroOrdenFilter !== "todos" && (
              <Tag closable onClose={() => handleNumeroOrdenFilter("todos")}>
                Numero de orden: {numeroOrdenFilter}
              </Tag>
            )}
          </Space>
        )}

        <Table
          rowKey={(r) => r.oidStockLote}
          loading={loading}
          columns={columns}
          dataSource={filtered}
          scroll={{ y: 500, x: 1800 }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: filtered.length,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} de ${total} registros`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
        />

        <Modal
          title="Detalle del Lote / PO"
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={null}
          width={820}
          style={{ top: 20 }}
        >
          {selected && (
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              {/* Estado del lote */}
              <Card
                size="small"
                style={{
                  background:
                    (selected.disponible_asignar ?? 0) > 0
                      ? "#f6ffed"
                      : "#fff2e8",
                }}
              >
                <Space>
                  {(selected.disponible_asignar ?? 0) > 0 ? (
                    <>
                      <CheckCircleOutlined
                        style={{ fontSize: 24, color: "#52c41a" }}
                      />
                      <div>
                        <Text strong style={{ color: "#52c41a" }}>
                          Stock Disponible
                        </Text>
                        <br />
                        <Text type="secondary">
                          Este lote tiene unidades disponibles para asignar
                        </Text>
                      </div>
                    </>
                  ) : (
                    <>
                      <WarningOutlined
                        style={{ fontSize: 24, color: "#fa8c16" }}
                      />
                      <div>
                        <Text strong style={{ color: "#fa8c16" }}>
                          Stock Agotado
                        </Text>
                        <br />
                        <Text type="secondary">
                          Este lote no tiene unidades disponibles
                        </Text>
                      </div>
                    </>
                  )}
                </Space>
              </Card>

              {/* Encabezado del Lote */}
              <Descriptions
                title={
                  <Space align="center">
                    <InboxOutlined />
                    <span>Información del Lote</span>
                  </Space>
                }
                bordered
                column={2}
                size="small"
              >
                <Descriptions.Item
                  label={
                    <Space>
                      <NumberOutlined /> oidStockLote
                    </Space>
                  }
                >
                  <Text code copyable>
                    {selected.oidStockLote}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <Space>
                      <BarcodeOutlined /> oidPo_Producto
                    </Space>
                  }
                >
                  <Text code copyable>
                    {selected.oidPo_Producto}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <Space>
                      <BarcodeOutlined /> oidPo
                    </Space>
                  }
                >
                  <Text code copyable>
                    {selected.oidPo}
                  </Text>
                </Descriptions.Item>
              </Descriptions>

              {/* Producto */}
              <Descriptions
                title={
                  <Space align="center">
                    <GoldOutlined />
                    <span>Producto (PO)</span>
                  </Space>
                }
                bordered
                column={1}
                size="small"
              >
                <Descriptions.Item label="Descripción">
                  <Paragraph style={{ margin: 0 }}>
                    {selected.productoPO || "—"}
                  </Paragraph>
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <Space>
                      <GoldOutlined /> Unidad de medida
                    </Space>
                  }
                >
                  <Tag color="blue">{selected.unidad_medida ?? "—"}</Tag>
                </Descriptions.Item>
              </Descriptions>

              {/* PO */}
              <Descriptions
                title={
                  <Space align="center">
                    <ShopOutlined />
                    <span>Orden de Compra</span>
                  </Space>
                }
                bordered
                column={3}
                size="small"
              >
                <Descriptions.Item label="Número de Orden">
                  <Typography.Text copyable={{ text: selected.numero_orden }}>
                    <Tag color="geekblue">{selected.numero_orden || "—"}</Tag>
                  </Typography.Text>
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <Space>
                      <CalendarOutlined /> Fecha PO
                    </Space>
                  }
                >
                  {selected.fecha_po
                    ? dayjs(selected.fecha_po).format("DD/MM/YYYY")
                    : "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Proveedor">
                  <Space direction="vertical" size={0}>
                    <Typography.Text strong>
                      {selected.proveedor_nombre || "—"}
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {selected.proveedor_identificacion || "—"}
                    </Typography.Text>
                  </Space>
                </Descriptions.Item>

                <Descriptions.Item label="Correo">
                  <Typography.Text>
                    {selected.proveedor_correo || "—"}
                  </Typography.Text>
                </Descriptions.Item>
                <Descriptions.Item label="Teléfono">
                  <Typography.Text>
                    {selected.proveedor_telefono || "—"}
                  </Typography.Text>
                </Descriptions.Item>
                <Descriptions.Item label="Dirección">
                  <Typography.Text>
                    {selected.proveedor_direccion || "—"}
                  </Typography.Text>
                </Descriptions.Item>
              </Descriptions>

              {/* Métricas */}
              <Row gutter={12}>
                <Col xs={24} sm={12} md={8}>
                  <Card size="small">
                    <Statistic
                      title="Cantidad PO"
                      value={selected.cantidad_po ?? 0}
                      precision={0}
                      groupSeparator="."
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Card size="small">
                    <Statistic
                      title="Entrada (lote)"
                      value={selected.cantidad_entrada ?? 0}
                      precision={0}
                      groupSeparator="."
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Card size="small">
                    <Statistic
                      title="Valor Unitario"
                      prefix={<DollarCircleOutlined />}
                      value={selected.valor_unitario ?? 0}
                      precision={2}
                      groupSeparator="."
                    />
                  </Card>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col xs={24} sm={12} md={8}>
                  <Card size="small">
                    <Statistic
                      title="Asignada"
                      value={selected.cantidad_asignada ?? 0}
                      precision={0}
                      groupSeparator="."
                      valueStyle={{ color: "#fa8c16" }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Card size="small">
                    <Statistic
                      title="Disponible"
                      value={selected.disponible_asignar ?? 0}
                      precision={0}
                      groupSeparator="."
                      valueStyle={{
                        color:
                          (selected.disponible_asignar ?? 0) > 0
                            ? "#3f8600"
                            : "#cf1322",
                      }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Card
                    size="small"
                    style={{ display: "flex", flexDirection: "column", gap: 8 }}
                  >
                    <Space
                      align="center"
                      style={{ justifyContent: "space-between" }}
                    >
                      <Space>
                        <PercentageOutlined />
                        <Text strong> Asignado</Text>
                      </Space>
                      {(selected.disponible_asignar ?? 0) > 0 ? (
                        <CheckCircleTwoTone twoToneColor="#52c41a" />
                      ) : (
                        <ExclamationCircleTwoTone twoToneColor="#fa541c" />
                      )}
                    </Space>
                    <Progress
                      percent={Math.max(
                        0,
                        Math.min(100, Number(selected.porcentaje_asignado || 0))
                      )}
                      size="small"
                      showInfo
                    />
                  </Card>
                </Col>
              </Row>

              <Divider style={{ margin: "8px 0" }} />

              {/* Resumen compact */}
              <Space wrap size="small">
                <Tag color="default">Lote: {selected.oidStockLote}</Tag>
                <Tag color="default">PO: {selected.numero_orden}</Tag>
                <Tag
                  color={
                    (selected.disponible_asignar ?? 0) > 0 ? "green" : "red"
                  }
                >
                  Disponible:{" "}
                  {(selected.disponible_asignar ?? 0).toLocaleString("es-CO")}
                </Tag>
                <Tag color="blue">Unidad: {selected.unidad_medida ?? "—"}</Tag>
              </Space>
            </Space>
          )}
        </Modal>
      </Card>
    </div>
  );
};

export default StockBodegas;
